'use client'

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { apiService, validateToken } from '@/lib/api'
import { UserPlus, UserMinus, Loader2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { useAuthCheck } from '@/hooks/use-auth-check'
import { useAuth } from '@/contexts/auth-context'
import AuthPopup from './auth-popup'

interface FollowButtonProps {
  userId: number
  isFollowing?: boolean
  onFollowChange?: (isFollowing: boolean) => void
  variant?: 'default' | 'outline' | 'ghost' | 'secondary'
  size?: 'default' | 'sm' | 'lg'
  className?: string
  showIcon?: boolean
}

export default function FollowButton({
  userId,
  isFollowing: initialIsFollowing,
  onFollowChange,
  variant = 'default',
  size = 'default',
  className = '',
  showIcon = true
}: FollowButtonProps) {
  const [isFollowing, setIsFollowing] = useState(initialIsFollowing || false)
  const [isLoading, setIsLoading] = useState(false)
  const { toast } = useToast()
  const { checkAuth, showAuthPopup, authAction, closeAuthPopup } = useAuthCheck()
  const { token, isAuthenticated, isLoading: authLoading, getToken } = useAuth()

  // Update internal state when prop changes
  useEffect(() => {
    if (initialIsFollowing !== undefined) {
      setIsFollowing(initialIsFollowing)
    }
  }, [initialIsFollowing])

  useEffect(() => {
    const fetchFollowStatus = async () => {
      // Don't make API calls while auth is loading
      if (authLoading) {
        return
      }

      // If initialIsFollowing is provided, use it and skip API call
      if (initialIsFollowing !== undefined) {
        return
      }

      const authToken = getToken();
      if (!isAuthenticated || !authToken) {
        setIsFollowing(false)
        return
      }

      try {
        const res: any = await apiService.checkFollowStatus(userId, authToken)

        if ('status' in res) {
          setIsFollowing(res.status === 'accepted' || res.is_following === true)
        } else if ('is_following' in res) {
          setIsFollowing(res.is_following === true)
        } else {
          setIsFollowing(false)
        }
      } catch (error: any) {
        console.error('Failed to fetch follow status:', error)
        // If it's an authentication error, the auth context will handle it
        if (error.message && error.message.includes('404')) {
          // If it's a 404 error, the endpoint doesn't exist, so default to not following
          setIsFollowing(false)
        } else {
          // For other errors, still default to not following but log the error
          setIsFollowing(false)
        }
      }
    }

    fetchFollowStatus()
  }, [userId, isAuthenticated, token, authLoading, initialIsFollowing])

  const handleFollowToggle = async () => {
    const performFollowAction = async () => {
      const authToken = getToken()
      if (!authToken) return

      console.log('Follow action - Token:', authToken ? 'Present' : 'Missing')
      setIsLoading(true)

      try {
        if (isFollowing) {
          await apiService.unfollowUser(userId, authToken)
          setIsFollowing(false)
          onFollowChange?.(false)
          toast({
            title: 'Unfollowed',
            description: 'You have unfollowed this user',
          })
        } else {
          await apiService.followUser(userId, authToken)
          setIsFollowing(true)
          onFollowChange?.(true)
          toast({
            title: 'Followed',
            description: 'You are now following this user',
          })
        }
      } catch (error) {
        console.error('Follow/Unfollow error:', error)
        toast({
          title: 'Error',
          description: 'Failed to follow/unfollow user. Please try again.',
          variant: 'destructive',
        })
      } finally {
        setIsLoading(false)
      }
    }

    await checkAuth("follow users", performFollowAction)
  }

  return (
    <>
      <Button
        variant={isFollowing ? 'outline' : variant}
        size={size}
        onClick={handleFollowToggle}
        disabled={isLoading}
        className={className}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : showIcon ? (
          isFollowing ? (
            <UserMinus className="h-4 w-4 mr-2" />
          ) : (
            <UserPlus className="h-4 w-4 mr-2" />
          )
        ) : null}
        {isFollowing ? 'Unfollow' : 'Follow'}
      </Button>
      
      <AuthPopup
        isOpen={showAuthPopup}
        onClose={closeAuthPopup}
        action={authAction}
      />
    </>
  )
}
